/**
 * Màn cấu hình tiệm: chi nhánh · dịch vụ · nhân viên (kèm ca làm việc).
 *
 * Tiêu chí đo được: **chủ tiệm thật tự dựng 1 chi nhánh + 10 dịch vụ + 3 KTV trong dưới 20 phút,
 * không cần ai hướng dẫn.** Các quyết định:
 *
 *   1. **Ba bước, đúng thứ tự phải làm**, đánh số: Chi nhánh → Dịch vụ → Nhân viên.
 *   2. **Giờ mở cửa**: nhập Thứ Hai rồi "chép cho cả tuần"; một ngày có **nhiều khoảng** được (nghỉ
 *      trưa). Bản cũ chỉ đọc khoảng đầu, lưu lại là **mất ca chiều** (gio-lam.test.ts).
 *   3. **Một dịch vụ là một dòng**: tên, giá, thời gian. "Đang bán" là công tắc, không phải nút
 *      "Ngừng dùng" trông như nút xoá.
 *   4. **Sửa nhân viên điền sẵn dịch vụ và chi nhánh đang giao** — lưu là thay toàn bộ, nên form
 *      rỗng từng xoá sạch dịch vụ của người đó (cau-hinh.int.test.ts ca 4b).
 *   5. Không `<input type="time">` (hiện "08:00 AM" trên máy tiếng Anh) — dùng `ChonGio` 24h.
 */

import { chuanHoaSdtVn, formatDuration } from '@sapa/shared'
import {
  Building2,
  Check,
  ChevronRight,
  ConciergeBell,
  Copy,
  Gift,
  HandCoins,
  KeyRound,
  LogOut,
  PackageOpen,
  Plus,
  Printer,
  QrCode,
  ReceiptText,
  Scissors,
  Settings2,
  Store,
  TicketPercent,
  ToggleRight,
  Trash2,
  UserPlus,
  Users,
  Wallet,
  X,
} from 'lucide-react'
import { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import { AnhQrSapa } from './AnhQrSapa.tsx'
import {
  ApiFailure,
  type ChiNhanhCH,
  caiDatApi,
  cauHinh,
  createApi,
  type DichVuCH,
  hienSdt,
  type LichVuongNghi,
  type MauCaCH,
  type NhanVienCH,
  type ToiLaAi,
  tienVnd,
} from './api.ts'
import { TheCaiDatCoc } from './CaiDatCoc.tsx'
import { TheCaiDatTiem } from './CaiDatTiem.tsx'
import { TheDiemCauHinh } from './ChamKhach.tsx'
import { datCaiDat, useTinhNang } from './cai-dat-tiem.ts'
import { DongTenTiem } from './chi-nhanh-dang-dung.tsx'
import { nhoSoMoi } from './dang-nhap-nho.ts'
import { FormDoiMa } from './FormDoiMa.tsx'
import { TheGoiSapa, TheZbsSuDung } from './GoiSapa.tsx'
import { TheGoiViCauHinh } from './GoiVi.tsx'
import { CAC_THU, gioLamSangTuan, type TuanGio, tuanSangGioLam } from './gio-lam.ts'
import { useHoiMaCuaToi } from './HoiMaCuaToi.tsx'
import { useHoiNgungNhan } from './HoiNgungNhan.tsx'
import { TheSanPhamCauHinh } from './Kho.tsx'
import { type LuatChiNhanh, luatCanGui, moChiNhanh, sauKhiLuu } from './luat-chi-nhanh.ts'
import { TheMaQr } from './MaQr.tsx'
import { CaiDatMayInPanel } from './MayIn.tsx'
import { CanhBaoLichVuong, MucNgayNghi } from './NgayNghi.tsx'
import { TheHoaHongCauHinh } from './SoTien.tsx'
import { TheTaiDuLieuKhach } from './TaiDuLieuKhach.tsx'
import { TheAnhCuaHang } from './TheAnhCuaHang.tsx'
import { TheThongTinTiem } from './ThongTinTiem.tsx'
import { MucTiemNghi } from './TiemNghi.tsx'
import { TheTinhNang } from './TinhNang.tsx'
import { C as CK } from './text/cham-khach.ts'
import { GS } from './text/goi-sapa.ts'
import { G } from './text/goi-vi.ts'
import { IN } from './text/in-hoa-don.ts'
import { K } from './text/kho.ts'
import { S } from './text/so-tien.ts'
import {
  cacChipChinhSach,
  chuDatToiThieu,
  chuHanDoiHuy,
  DAT_TOI_THIEU,
  DAT_TOI_THIEU_MAC_DINH_PHUT,
  DAT_TRUOC,
  DAT_TRUOC_MAC_DINH_NGAY,
  HAN_DOI_HUY,
  HAN_DOI_HUY_MAC_DINH_PHUT,
  LUAT_DAT_LICH,
  MUC_DAT_TOI_THIEU_PHUT,
  MUC_DAT_TRUOC_NGAY,
  MUC_HAN_DOI_HUY_PHUT,
  nhomDangDung,
  tenVaiHienThi,
} from './text/theo-nganh.ts'
import { TT } from './text/thong-tin-tiem.ts'
import { TN } from './text/tinh-nang.ts'
import { U } from './text/uu-dai.ts'
import { T } from './text/vi.ts'
import {
  canMaKhiDoiSo,
  canMaKhiLuu,
  hienOMaXacNhan,
  mayChuDoiMa,
  viecOMa,
  viecTrenTo,
} from './tu-khoa-minh.ts'
import { TheUuDaiCauHinh } from './UuDai.tsx'
import { Avatar, CacCum, ChonGio, CongTac, Rong, ThongBao, To, Xuong } from './ui.tsx'
import { caMacDinhTuGioLam, canTaoChiNhanhTruoc } from './xep-ca-nguoi-moi.ts'

export type TheCauHinh =
  | 'chi-nhanh'
  | 'dich-vu'
  | 'nhan-vien'
  | 'goi-vi'
  | 'san-pham'
  | 'hoa-hong'
  | 'uu-dai'
  | 'diem'
  | 'ma-qr'
  | 'cai-dat'
  | 'may-in'
  | 'tinh-nang'
  | 'goi-sapa'
  | 'thong-tin'

/** Toast lỗi tự đóng sau chừng này mili giây (lâu hơn toast thành công 3,5 giây: lỗi cần thời gian đọc và sửa). */
export const THOI_GIAN_LOI_MS = 8000

/**
 * Câu lỗi của MỌI thẻ Cấu hình (`bao()` → `setLoi`): toast NỔI (cố định ở đáy màn, như toast "Đã lưu") thay cho thanh báo ở đầu
 * khung cuộn. Trước đây bấm "Lưu chi nhánh" ở cuối trang gặp 409 thì câu lỗi nằm cách màn hình gần 1.000px phía trên — người dùng
 * không thấy gì (soát giao diện vòng 2, B2). `role="alert"` để đọc ngay; tự đóng sau `THOI_GIAN_LOI_MS`; có nút đóng ≥ 48px.
 */
export function LoiCauHinh({ loi, onDong }: { loi: string | null; onDong: () => void }) {
  useEffect(() => {
    if (loi === null) return
    const t = setTimeout(onDong, THOI_GIAN_LOI_MS)
    return () => clearTimeout(t)
  }, [loi, onDong])
  if (loi === null) return null
  return (
    <ThongBao loai="loi" noi khan onDong={onDong}>
      {loi}
    </ThongBao>
  )
}

const THU_VN = ['', 'Thứ Hai', 'Thứ Ba', 'Thứ Tư', 'Thứ Năm', 'Thứ Sáu', 'Thứ Bảy', 'Chủ Nhật']
const VAI_CHON = [
  { ma: 'technician', ten: 'Kỹ thuật viên' },
  { ma: 'cashier', ten: 'Lễ tân' },
  { ma: 'manager', ten: 'Quản lý' },
  { ma: 'owner', ten: 'Chủ tiệm' },
]
/** 4.3: biểu tượng "Dịch vụ" — kéo cắt chỉ hợp tiệm làm đẹp; nhóm dịch vụ dùng biểu tượng chung. */
function bieuTuongDichVu(): typeof Scissors {
  return nhomDangDung() === 'dich-vu' ? ConciergeBell : Scissors
}
const THOI_LUONG = Array.from({ length: 16 }, (_, i) => (i + 1) * 15)
/** Tối đa 3 khoảng mở cửa một ngày — khớp trần 21 khoảng/tuần của máy chủ. */
const TOI_DA_KHOANG = 3

interface ChungProps {
  onLoi: (e: unknown) => void
  onXong: (cau: string) => void
}

export function CauHinh({
  toi,
  the,
  onDoiThe,
  laChuTiem,
  cuonTaiKhoan = false,
  onDaCuonTaiKhoan,
  onDoiThongTin,
  onVeLich,
  onXemTrenLich,
}: {
  toi: ToiLaAi
  the: TheCauHinh
  onDoiThe: (t: TheCauHinh) => void
  laChuTiem: boolean
  /** Tờ thu bảo khai tài khoản nhận tiền: thẻ "Tài khoản nhận tiền" (Cài đặt tiệm) cuộn tới đầu màn một lần. */
  cuonTaiKhoan?: boolean
  onDaCuonTaiKhoan?: () => void
  /** Chủ tiệm vừa đổi tên / ngành → tải lại `toi` để tên ở đầu màn đổi theo. */
  onDoiThongTin: () => void
  /** Mã QR chưa Live: nút "Đặt lịch ở quầy" đưa chủ tiệm về màn Lịch hẹn. */
  onVeLich?: () => void
  /** BK-2: cảnh báo "lịch vướng" sau khi lưu — bấm lịch mở Lịch đúng ngày + chi nhánh và tờ "Xử lý". Thiếu quyền → không nút. */
  onXemTrenLich?: (ngay: string, chiNhanhId: string) => void
}) {
  const [loi, setLoi] = useState<string | null>(null)
  const [xong, setXong] = useState<string | null>(null)
  /**
   * Đang gõ nhân viên mới mà tiệm chưa có chi nhánh → sang tạo chi nhánh: giữ tên + số đã gõ để sau khi lưu chi
   * nhánh, bấm "Quay lại thêm nhân viên" là mở lại tờ đúng chỗ cũ (vòng sửa UX Đợt 1; trước đây mất sạch).
   */
  const [nhapNhanVien, setNhapNhanVien] = useState<{ ten: string; sdt: string } | null>(null)
  const [apiGoiVi] = useState(() => createApi(toi.tiem))
  // Điện thoại: dải bước cuộn ngang — bước đang chọn luôn nằm giữa, mép nào còn chữ thì mờ mép đó.
  const dsBuoc = useRef<HTMLElement | null>(null)
  const [moTrai, setMoTrai] = useState(false)
  const [moPhai, setMoPhai] = useState(false)
  const capNhatMo = useCallback((el: HTMLElement) => {
    setMoTrai(el.scrollLeft > 4)
    setMoPhai(el.scrollLeft + el.clientWidth < el.scrollWidth - 4)
  }, [])
  // biome-ignore lint/correctness/useExhaustiveDependencies: `the` đổi thì bước đang chọn đổi — cuộn lại tới nó.
  useEffect(() => {
    const el = dsBuoc.current
    if (el === null) return
    const nut = el.querySelector<HTMLElement>('[aria-current="page"]')
    if (nut !== null && el.scrollWidth > el.clientWidth) {
      el.scrollLeft = nut.offsetLeft - (el.clientWidth - nut.offsetWidth) / 2
    }
    capNhatMo(el)
  }, [the, capNhatMo])

  /*
   * `useCallback` ở đây KHÔNG phải để tối ưu tốc độ: hai hàm này nằm trong mảng phụ thuộc của
   * `useEffect` nạp dữ liệu ở các thẻ con. Tạo mới mỗi lần vẽ → effect chạy lại → vòng lặp gọi máy chủ.
   */
  const bao = useCallback((e: unknown) => {
    setXong(null)
    setLoi(e instanceof ApiFailure ? e.messageVi : T.serverFailed)
  }, [])
  const ok = useCallback((cau: string) => {
    setLoi(null)
    setXong(cau)
  }, [])
  // Ổn định giữa các lần vẽ: bộ đếm tự đóng của `LoiCauHinh` không bị đặt lại mỗi lần màn vẽ lại.
  const dongLoi = useCallback(() => setLoi(null), [])

  useEffect(() => {
    if (xong === null) return
    const t = setTimeout(() => setXong(null), 3500)
    return () => clearTimeout(t)
  }, [xong])

  // Công tắc tính năng (ADR-074): tắt thì mục biến khỏi menu, số thứ tự tự dồn. Chưa tải xong → hiện như cũ.
  const coKho = useTinhNang('kho')
  const coDiem = useTinhNang('diem')
  const coGoiVi = useTinhNang('goi-vi')
  const coHoaHong = useTinhNang('hoa-hong')
  const coUuDai = useTinhNang('uu-dai')
  /*
   * Hai nhóm (phản ánh 25/09): "Tiệm" — dựng tiệm, chủ và quản lý cùng làm; "Chỉ chủ tiệm" — tiền
   * công, quyền, cài đặt. Trước đây hai loại trộn một danh sách đánh số 1–9, quản lý không biết mục
   * nào mình không có.
   */
  const menu: { ma: TheCauHinh; ten: string; icon: typeof Building2; nhom: 'tiem' | 'chu' }[] = [
    { ma: 'chi-nhanh', ten: T.tabBranch, icon: Building2, nhom: 'tiem' },
    { ma: 'dich-vu', ten: T.tabService, icon: bieuTuongDichVu(), nhom: 'tiem' },
    { ma: 'nhan-vien', ten: T.tabStaff, icon: Users, nhom: 'tiem' },
    ...(coGoiVi
      ? [{ ma: 'goi-vi' as const, ten: G.tab, icon: Wallet, nhom: 'tiem' as const }]
      : []),
    ...(coKho
      ? [{ ma: 'san-pham' as const, ten: K.tabCauHinh, icon: PackageOpen, nhom: 'tiem' as const }]
      : []),
    { ma: 'ma-qr', ten: T.tabQr, icon: QrCode, nhom: 'tiem' },
    // In hoá đơn: máy in của CHÍNH thiết bị đang dùng (USB / Bluetooth / hộp in hệ điều hành).
    { ma: 'may-in', ten: IN.tieuDeMayIn, icon: Printer, nhom: 'tiem' },
    // Tên + ngành của tiệm (docs/plans/thong-tin-tiem.md): chỉ chủ tiệm, đầu nhóm "Chỉ chủ tiệm".
    ...(laChuTiem
      ? [{ ma: 'thong-tin' as const, ten: TT.tab, icon: Store, nhom: 'chu' as const }]
      : []),
    // Hoa hồng + bảng tháng, điểm, cài đặt: chỉ chủ tiệm (quyền `cau-hinh.nhan-vien`).
    ...(laChuTiem && coHoaHong
      ? [{ ma: 'hoa-hong' as const, ten: S.tabHoaHong, icon: HandCoins, nhom: 'chu' as const }]
      : []),
    ...(laChuTiem && coDiem
      ? [{ ma: 'diem' as const, ten: CK.diemTieuDe, icon: Gift, nhom: 'chu' as const }]
      : []),
    // KH-GĐ2 GĐ3: ưu đãi chào khách mới qua QR (công tắc "Ưu đãi").
    ...(laChuTiem && coUuDai
      ? [{ ma: 'uu-dai' as const, ten: U.tab, icon: TicketPercent, nhom: 'chu' as const }]
      : []),
    ...(laChuTiem
      ? [
          { ma: 'tinh-nang' as const, ten: TN.tab, icon: ToggleRight, nhom: 'chu' as const },
          { ma: 'cai-dat' as const, ten: T.tabSettings, icon: Settings2, nhom: 'chu' as const },
        ]
      : []),
    // Phản ánh 25/09 đợt 4: chủ tiệm xem hạn dùng gói SAPA + tiền tin Zalo đã tiêu vào đâu.
    ...(toi.quyen.includes('xem.goi-sapa')
      ? [{ ma: 'goi-sapa' as const, ten: GS.tab, icon: ReceiptText, nhom: 'chu' as const }]
      : []),
  ]

  return (
    <>
      <header className="dau-man">
        <div className="ten-man">
          <DongTenTiem tenTiem={toi.tenTiem} theoChiNhanh={false} />
          <h1>{T.pageConfig}</h1>
        </div>
      </header>
      <main className="than-man">
        <div className="cau-hinh-luoi">
          <nav
            className="the menu-cau-hinh"
            aria-label={T.pageConfig}
            ref={dsBuoc}
            data-mo-trai={moTrai ? '1' : '0'}
            data-mo-phai={moPhai ? '1' : '0'}
            onScroll={(e) => capNhatMo(e.currentTarget)}
          >
            {menu.map((m, i) => (
              <Fragment key={m.ma}>
                {m.nhom !== menu[i - 1]?.nhom && (
                  <span className="nhom-menu an-dien-thoai">
                    {m.nhom === 'tiem' ? T.configGroupShop : T.configGroupOwner}
                  </span>
                )}
                <button
                  type="button"
                  className={the === m.ma ? 'dang-chon' : ''}
                  aria-current={the === m.ma ? 'page' : undefined}
                  onClick={() => {
                    onDoiThe(m.ma)
                    setLoi(null)
                    setXong(null)
                  }}
                >
                  <span className="so-buoc">{i + 1}</span>
                  {m.ten}
                  <ChevronRight
                    size={18}
                    className="an-dien-thoai"
                    style={{ marginLeft: 'auto' }}
                  />
                </button>
              </Fragment>
            ))}
          </nav>
          <div className="cot-cau-hinh">
            {the === 'chi-nhanh' && (
              <TheChiNhanh
                onLoi={bao}
                onXong={ok}
                {...(onXemTrenLich === undefined ? {} : { onXemTrenLich })}
                {...(nhapNhanVien === null ? {} : { quayLaiNhanVien: () => onDoiThe('nhan-vien') })}
              />
            )}
            {the === 'dich-vu' && <TheDichVu onLoi={bao} onXong={ok} />}
            {the === 'ma-qr' && (
              <TheMaQr toi={toi} {...(onVeLich === undefined ? {} : { onVeLich })} />
            )}
            {the === 'goi-vi' && coGoiVi && <TheGoiViCauHinh toi={toi} api={apiGoiVi} />}
            {the === 'hoa-hong' && laChuTiem && coHoaHong && <TheHoaHongCauHinh api={apiGoiVi} />}
            {the === 'san-pham' && coKho && <TheSanPhamCauHinh api={apiGoiVi} />}
            {the === 'diem' && laChuTiem && coDiem && <TheDiemCauHinh api={apiGoiVi} />}
            {the === 'uu-dai' && laChuTiem && coUuDai && <TheUuDaiCauHinh api={apiGoiVi} />}
            {the === 'tinh-nang' && laChuTiem && <TheTinhNang onLoi={bao} onXong={ok} />}
            {the === 'nhan-vien' && (
              <TheNhanVien
                onLoi={bao}
                onXong={ok}
                laChuTiem={laChuTiem}
                onTaoChiNhanh={(nhap) => {
                  setNhapNhanVien(nhap)
                  onDoiThe('chi-nhanh')
                }}
                nhapDau={nhapNhanVien}
                onDaDungNhap={() => setNhapNhanVien(null)}
                onMoChiNhanh={() => onDoiThe('chi-nhanh')}
                toiId={toi.toi.id}
                {...(onXemTrenLich === undefined ? {} : { onXemTrenLich })}
              />
            )}
            {the === 'cai-dat' && laChuTiem && <TheCaiDatTiem onLoi={bao} onXong={ok} />}
            {the === 'thong-tin' && laChuTiem && (
              <>
                <TheThongTinTiem toi={toi} onLoi={bao} onXong={ok} onDaLuu={onDoiThongTin} />
                {/* v0.23.3: chỉ chủ (khối này) VÀ chỉ khi máy chủ báo `toi.anhCuaHang.bat` — thẻ tự ẩn nếu không. */}
                <TheAnhCuaHang toi={toi} onDaLuu={onDoiThongTin} />
                <TheTaiDuLieuKhach onLoi={bao} onXong={ok} />
              </>
            )}
            {the === 'may-in' && (
              <section className="the the-dem the-khoi">
                <h2>{IN.tieuDeMayIn}</h2>
                <CaiDatMayInPanel tenTiem={toi.tenTiem} />
              </section>
            )}
            {the === 'goi-sapa' && toi.quyen.includes('xem.goi-sapa') && (
              <>
                <TheGoiSapa onLoi={bao} />
                <TheZbsSuDung onLoi={bao} />
              </>
            )}
            {the === 'cai-dat' && laChuTiem && (
              <TheCaiDatCoc
                api={apiGoiVi}
                onLoi={bao}
                onXong={ok}
                cuon={cuonTaiKhoan}
                {...(onDaCuonTaiKhoan === undefined ? {} : { onDaCuon: onDaCuonTaiKhoan })}
              />
            )}
          </div>
        </div>
      </main>
      {xong !== null && (
        <ThongBao loai="thanh-cong" noi onDong={() => setXong(null)}>
          {xong}
        </ThongBao>
      )}
      {/* Lỗi cũng nổi cạnh người bấm (B2): `bao()` luôn xoá `xong`, `ok()` luôn xoá `loi` nên hai toast không bao giờ chồng nhau. */}
      <LoiCauHinh loi={loi} onDong={dongLoi} />
    </>
  )
}

function KhungTai() {
  return (
    <div className="the the-dem" style={{ display: 'grid', gap: 14 }}>
      <Xuong cao={22} rong="40%" />
      <Xuong cao={48} />
      <Xuong cao={48} />
      <Xuong cao={48} />
    </div>
  )
}

// =============================================================================== Chi nhánh
function TheChiNhanh({
  onLoi,
  onXong,
  quayLaiNhanVien,
  onXemTrenLich,
}: ChungProps & {
  /** Có khi chủ tiệm tới đây từ tờ "Thêm nhân viên" (chưa có chi nhánh): lưu xong hiện nút quay lại. */
  quayLaiNhanVien?: () => void
  onXemTrenLich?: (ngay: string, chiNhanhId: string) => void
}) {
  const hoi = useHoiNgungNhan()
  const [daLuu, setDaLuu] = useState(false)
  /** BK-2: lịch nằm ngoài giờ mở cửa mới (đã lưu rồi mới liệt kê). */
  const [vuong, setVuong] = useState<{ ds: LichVuongNghi[]; tong: number } | null>(null)
  const [ds, setDs] = useState<ChiNhanhCH[] | null>(null)
  const [chon, setChon] = useState<string | null>(null)
  const [ten, setTen] = useState('')
  const [diaChi, setDiaChi] = useState('')
  const [sdt, setSdt] = useState('')
  /** 4.2: khách đặt trước tối đa mấy ngày (máy chủ cũ không trả → mặc định 45 như cột DB). */
  const [ngayDatTruoc, setNgayDatTruoc] = useState<number>(DAT_TRUOC_MAC_DINH_NGAY)
  /** K5: khách đặt trước ít nhất / tự đổi-huỷ được tới bao lâu trước giờ hẹn (phút; máy chủ cũ không trả → 30 / 120). */
  const [datToiThieu, setDatToiThieu] = useState<number>(DAT_TOI_THIEU_MAC_DINH_PHUT)
  const [hanDoiHuy, setHanDoiHuy] = useState<number>(HAN_DOI_HUY_MAC_DINH_PHUT)
  /** Luật lúc TẢI chi nhánh (hoặc lúc lưu gần nhất): chỉ gửi trường chủ tiệm THỰC SỰ đổi — tab cũ không ghi đè (B7). */
  const [gocLuat, setGocLuat] = useState<LuatChiNhanh | null>(null)
  const [tuan, setTuan] = useState<TuanGio>(() => gioLamSangTuan([]))
  const [dangLuu, setDangLuu] = useState(false)
  // ADR-074: "Nhiều chi nhánh" tắt → không có nút thêm (tiệm chưa có chi nhánh nào vẫn tạo được cái đầu).
  const coNhieuChiNhanh = useTinhNang('chi-nhanh')

  const mo = useCallback((cn: ChiNhanhCH | null) => {
    setChon(cn?.id ?? null)
    setTen(cn?.ten ?? '')
    setDiaChi(cn?.diaChi ?? '')
    setSdt(cn === null ? '' : hienSdt(cn.sdt))
    const l = moChiNhanh(cn)
    setNgayDatTruoc(l.o.ngayDatTruoc)
    setDatToiThieu(l.o.datTruocToiThieuPhut)
    setHanDoiHuy(l.o.hanHuyPhut)
    setGocLuat(l.goc)
    // Chi nhánh mới: gợi ý 09:00–20:00 cả tuần — chủ tiệm chỉ sửa chỗ khác, không gõ từ đầu.
    setTuan(
      cn === null
        ? gioLamSangTuan(CAC_THU.map((thu) => ({ thu, moMin: 540, dongMin: 1200 })))
        : gioLamSangTuan(cn.gioLam),
    )
  }, [])

  useEffect(() => {
    void cauHinh
      .chiNhanh()
      .then((r) => {
        setDs(r.chiNhanh)
        mo(r.chiNhanh[0] ?? null)
      })
      .catch(onLoi)
  }, [onLoi, mo])

  /** Lưu cả thông tin lẫn giờ mở cửa bằng MỘT nút — hai nút "Lưu" trên một màn là hai lần đoán. */
  async function luu() {
    setDangLuu(true)
    try {
      const r = await cauHinh.luuChiNhanh({
        ...(chon === null ? {} : { id: chon }),
        ten: ten.trim(),
        diaChi: diaChi.trim() === '' ? null : diaChi.trim(),
        sdt: sdt.trim() === '' ? null : sdt.trim(),
        ...luatCanGui(
          { ngayDatTruoc, datTruocToiThieuPhut: datToiThieu, hanHuyPhut: hanDoiHuy },
          gocLuat,
        ),
      })
      // B5: giờ mở cửa rỗng làm chi nhánh không mở ngày nào → máy chủ hỏi lại; "Thôi" giữ giờ cũ (thông tin chi nhánh đã lưu ở bước trên).
      const kq = await hoi.chay((xn) => cauHinh.luuGioLam(r.id, tuanSangGioLam(tuan), xn))
      if (kq.ket === 'thoi') {
        setDs(r.chiNhanh)
        setChon(r.id)
        const lc = sauKhiLuu(r.chiNhanh, r.id)
        setNgayDatTruoc(lc.o.ngayDatTruoc)
        setDatToiThieu(lc.o.datTruocToiThieuPhut)
        setHanDoiHuy(lc.o.hanHuyPhut)
        setGocLuat(lc.goc)
        return
      }
      const g = kq.gia
      const dsVuong = g.lichVuong ?? []
      setVuong(dsVuong.length > 0 ? { ds: dsVuong, tong: g.soLichVuong ?? dsVuong.length } : null)
      setDs(g.chiNhanh)
      setChon(r.id)
      // Sau khi lưu: CẢ BA ô và gốc = những gì máy chủ đang giữ (máy khác có thể vừa đổi luật). Chỉ đặt gốc mà
      // để ô cũ thì lần lưu sau tab này gửi số cũ đè lên luật vừa đổi (K5 vòng 2, B3).
      const l = sauKhiLuu(g.chiNhanh, r.id)
      setNgayDatTruoc(l.o.ngayDatTruoc)
      setDatToiThieu(l.o.datTruocToiThieuPhut)
      setHanDoiHuy(l.o.hanHuyPhut)
      setGocLuat(l.goc)
      setDaLuu(true)
      onXong(T.saved)
    } catch (e) {
      onLoi(e)
    } finally {
      setDangLuu(false)
    }
  }

  function doiKhoang(thu: number, i: number, sua: Partial<{ mo: number; dong: number }>) {
    setTuan((t) => ({
      ...t,
      [thu]: (t[thu] ?? []).map((k, j) => (j === i ? { ...k, ...sua } : k)),
    }))
  }

  if (ds === null) return <KhungTai />

  const thu2 = tuan[1] ?? []
  return (
    <>
      {hoi.hop}
      {quayLaiNhanVien !== undefined && daLuu && (
        <ThongBao
          loai="thanh-cong"
          lon
          hanhDong={
            <button type="button" className="nut nut-chinh nut-lon" onClick={quayLaiNhanVien}>
              {T.staffBackToAdd}
            </button>
          }
        >
          {T.branchSavedBack}
        </ThongBao>
      )}
      {/* "Thêm chi nhánh" nằm ở hàng chọn chi nhánh, KHÔNG trong thẻ đang sửa (vòng 1). */}
      {
        <div className="hang-chip cuon">
          {ds.map((cn) => (
            <button
              key={cn.id}
              type="button"
              className={`chip ${chon === cn.id ? 'dang-chon' : ''}`}
              onClick={() => mo(cn)}
            >
              <Building2 size={16} />
              {cn.ten}
            </button>
          ))}
          {(coNhieuChiNhanh || ds.length === 0) && (
            <button
              type="button"
              className={`chip ${chon === null ? 'dang-chon' : ''}`}
              onClick={() => mo(null)}
            >
              <Plus size={16} />
              {T.branchAdd}
            </button>
          )}
        </div>
      }

      <section className="the the-dem">
        <div className="the-tieu-de">
          <div>
            <h2>{T.branchInfo}</h2>
            <p>{T.branchInfoHint}</p>
          </div>
        </div>
        <div className="luoi-form">
          <div className="truong ca-hang">
            <label className="nhan" htmlFor="cn-ten">
              {T.branchName}
            </label>
            <input
              id="cn-ten"
              className="o-nhap"
              value={ten}
              placeholder={T.branchNameExample}
              onChange={(e) => setTen(e.target.value)}
            />
          </div>
          <div className="truong">
            <label className="nhan" htmlFor="cn-dc">
              {T.branchAddress}
            </label>
            <input
              id="cn-dc"
              className="o-nhap"
              value={diaChi}
              placeholder={T.branchAddressExample}
              onChange={(e) => setDiaChi(e.target.value)}
            />
          </div>
          <div className="truong">
            <label className="nhan" htmlFor="cn-sdt">
              {T.branchPhone}
            </label>
            <input
              id="cn-sdt"
              className="o-nhap"
              value={sdt}
              placeholder={T.phonePlaceholder}
              onChange={(e) => setSdt(e.target.value)}
              inputMode="tel"
            />
          </div>
          <h3 className="tieu-de-nho ca-hang">{LUAT_DAT_LICH.tieuDe}</h3>
          <div className="truong ca-hang">
            <span className="nhan">{DAT_TRUOC.nhan}</span>
            <div className="hang-chip chip-dat-truoc">
              {/* Giá trị khác 5 mức (đặt từ trước ở DB) vẫn hiện một chip riêng — không mất lựa chọn đang dùng. */}
              {[...new Set<number>([...MUC_DAT_TRUOC_NGAY, ngayDatTruoc])]
                .sort((a, b) => a - b)
                .map((n) => (
                  <button
                    key={n}
                    type="button"
                    className={`chip ${ngayDatTruoc === n ? 'dang-chon' : ''}`}
                    aria-pressed={ngayDatTruoc === n}
                    onClick={() => setNgayDatTruoc(n)}
                  >
                    {ngayDatTruoc === n && <Check size={18} />}
                    {DAT_TRUOC.soNgay(n)}
                  </button>
                ))}
            </div>
            <p className="goi-y-dat-truoc">{DAT_TRUOC.goiY}</p>
          </div>
          <div className="truong ca-hang">
            <span className="nhan">{DAT_TOI_THIEU.nhan}</span>
            <div className="hang-chip chip-dat-truoc">
              {cacChipChinhSach(MUC_DAT_TOI_THIEU_PHUT, datToiThieu, chuDatToiThieu).map((c) => (
                <button
                  key={c.phut}
                  type="button"
                  className={`chip ${datToiThieu === c.phut ? 'dang-chon' : ''}`}
                  aria-pressed={datToiThieu === c.phut}
                  onClick={() => setDatToiThieu(c.phut)}
                >
                  {datToiThieu === c.phut && <Check size={18} />}
                  {c.chu}
                </button>
              ))}
            </div>
            <p className="goi-y-dat-truoc">{DAT_TOI_THIEU.goiY}</p>
          </div>
          <div className="truong ca-hang">
            <span className="nhan">{HAN_DOI_HUY.nhan}</span>
            <div className="hang-chip chip-dat-truoc">
              {cacChipChinhSach(MUC_HAN_DOI_HUY_PHUT, hanDoiHuy, chuHanDoiHuy).map((c) => (
                <button
                  key={c.phut}
                  type="button"
                  className={`chip ${hanDoiHuy === c.phut ? 'dang-chon' : ''}`}
                  aria-pressed={hanDoiHuy === c.phut}
                  onClick={() => setHanDoiHuy(c.phut)}
                >
                  {hanDoiHuy === c.phut && <Check size={18} />}
                  {c.chu}
                </button>
              ))}
            </div>
            <p className="goi-y-dat-truoc">{HAN_DOI_HUY.goiY}</p>
          </div>
        </div>
      </section>

      <section className="the the-dem">
        <div className="the-tieu-de">
          <div>
            <h2>{T.hoursTitle}</h2>
            <p>{T.hoursHint}</p>
          </div>
          <button
            type="button"
            className="nut nut-vien"
            disabled={thu2.length === 0}
            onClick={() =>
              setTuan((t) => {
                const moi = { ...t }
                for (const thu of CAC_THU) if (thu !== 1) moi[thu] = thu2.map((k) => ({ ...k }))
                return moi
              })
            }
          >
            <Copy size={18} />
            {T.hoursCopyAll}
          </button>
        </div>
        <div className="ds-thu">
          {CAC_THU.map((thu) => {
            const cac = tuan[thu] ?? []
            return (
              <div key={thu} className="hang-thu">
                <CongTac
                  bat={cac.length > 0}
                  nhan={<b>{THU_VN[thu]}</b>}
                  onDoi={(bat) =>
                    setTuan((t) => ({ ...t, [thu]: bat ? [{ mo: 540, dong: 1200 }] : [] }))
                  }
                />
                <div className="cac-khoang">
                  {cac.length === 0 && <span className="nghi">{T.hoursClosedDay}</span>}
                  {cac.map((k, i) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: khoảng giờ không có id; khoá theo giờ thì đổi giờ là dựng lại ô chọn và mất tiêu điểm giữa lúc đang chọn.
                    <span key={`${thu}-${i}`} className="khoang">
                      <ChonGio
                        phut={k.mo}
                        nhan={`${THU_VN[thu]} · ${T.hoursOpen}`}
                        onDoi={(p) => doiKhoang(thu, i, { mo: p })}
                      />
                      <span className="gach">–</span>
                      <ChonGio
                        phut={k.dong}
                        nhan={`${THU_VN[thu]} · ${T.hoursClose}`}
                        onDoi={(p) => doiKhoang(thu, i, { dong: p })}
                      />
                      {cac.length === 1 && <span className="giu-cho-x" />}
                      {cac.length > 1 && (
                        <button
                          type="button"
                          className="nut nut-nhat nut-icon"
                          aria-label={T.hoursRemoveRange}
                          onClick={() =>
                            setTuan((t) => ({
                              ...t,
                              [thu]: (t[thu] ?? []).filter((_, j) => j !== i),
                            }))
                          }
                        >
                          <X size={18} />
                        </button>
                      )}
                    </span>
                  ))}
                  {cac.length > 0 && cac.length < TOI_DA_KHOANG && (
                    <button
                      type="button"
                      className="nut nut-nhat"
                      onClick={() =>
                        setTuan((t) => {
                          const cu = t[thu] ?? []
                          const cuoi = cu[cu.length - 1]?.dong ?? 780
                          const mo = Math.min(cuoi + 60, 1380)
                          return { ...t, [thu]: [...cu, { mo, dong: Math.min(mo + 240, 1440) }] }
                        })
                      }
                    >
                      <Plus size={16} />
                      {T.hoursAddRange}
                    </button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
        <div className="hang-nut-cuoi">
          <button
            type="button"
            className="nut nut-chinh nut-lon"
            disabled={dangLuu || ten.trim() === ''}
            onClick={() => void luu()}
          >
            {dangLuu ? T.savingText : T.branchSave}
          </button>
        </div>
        {vuong !== null && (
          <CanhBaoLichVuong
            loiDau={T.hoursSavedBusy}
            ds={vuong.ds}
            tong={vuong.tong}
            {...(onXemTrenLich === undefined
              ? {}
              : { onMo: (v: LichVuongNghi) => onXemTrenLich(v.batDau.slice(0, 10), v.branchId) })}
          />
        )}
      </section>
      {/* K2 (ADR-111): chỉ khi chi nhánh đã lưu (cần id). Thẻ riêng, nút riêng — hành động có hiệu lực ngay. */}
      {chon !== null && (
        <MucTiemNghi
          key={chon}
          branchId={chon}
          ngayDong={ds.find((c) => c.id === chon)?.ngayDong ?? []}
          onDoi={setDs}
          onLoi={onLoi}
          onXong={onXong}
          {...(onXemTrenLich === undefined ? {} : { onXemTrenLich })}
        />
      )}
    </>
  )
}

// ================================================================================= Dịch vụ
/** N3: lựa chọn "nhắc khách quay lại sau … ngày" (máy chủ nhận 7–180). */
const CHU_KY_QUAY_LAI = [7, 14, 21, 28, 35, 45, 60, 90] as const

function TheDichVu({ onLoi, onXong }: ChungProps) {
  const hoi = useHoiNgungNhan()
  const [ds, setDs] = useState<DichVuCH[] | null>(null)
  const [ten, setTen] = useState('')
  const [gia, setGia] = useState('')
  const [phut, setPhut] = useState(60)
  /** N3: nhắc khách quay lại sau … ngày; `null` = mặc định theo ngành. */
  const [quayLai, setQuayLai] = useState<number | null>(null)
  const [dangLuu, setDangLuu] = useState(false)
  /** Dịch vụ đang sửa; `null` = đang thêm mới. */
  const [dangSua, setDangSua] = useState<DichVuCH | null>(null)
  /**
   * Điện thoại (vòng 48): form thêm gập sau nút "Thêm dịch vụ" — chủ vào đây để xem lại giá thì thấy danh sách ngay,
   * không phải cuộn qua 4 ô. Tablet / máy tính vẫn hiện form như cũ. Danh sách còn trống thì form mở sẵn.
   */
  const [moForm, setMoForm] = useState(false)

  function moSua(d: DichVuCH | null) {
    setDangSua(d)
    if (d === null) setMoForm(false)
    setTen(d?.ten ?? '')
    setGia(d?.giaVnd ?? '')
    setPhut(d?.phut ?? 60)
    setQuayLai(d?.quayLaiNgay ?? null)
    if (d !== null) window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  useEffect(() => {
    void cauHinh
      .dichVu()
      .then((r) => setDs(r.dichVu))
      .catch(onLoi)
  }, [onLoi])

  // Vừa mở form thêm (điện thoại): con trỏ vào ô tên để gõ luôn.
  useEffect(() => {
    if (moForm) document.getElementById('dv-ten')?.focus()
  }, [moForm])

  // Người dùng gõ "150.000" hoặc "150000" đều được; chỉ giữ chữ số. Tiền đi lên máy chủ dưới dạng
  // CHUỖI số nguyên đồng (ADR-026) — không qua `Number`.
  const giaSo = gia.replace(/\D/g, '').replace(/^0+(?=\d)/, '')
  const BieuTuong = bieuTuongDichVu()

  async function them(e: React.FormEvent) {
    e.preventDefault()
    setDangLuu(true)
    try {
      const r = await cauHinh.luuDichVu({
        ...(dangSua === null
          ? {}
          : {
              serviceId: dangSua.serviceId,
              variantId: dangSua.variantId,
              // GỬI LẠI đệm cũ: máy chủ mặc định 0 khi thiếu — form không hiện trường này, nên
              // thiếu nó là xoá đệm của dịch vụ mỗi lần sửa giá (cùng họ lỗi "form rỗng xoá dữ liệu").
              demSauPhut: dangSua.demSauPhut,
            }),
        ten: ten.trim(),
        giaVnd: giaSo === '' ? '0' : giaSo,
        phut,
        quayLaiNgay: quayLai,
      })
      setDs(r.dichVu)
      moSua(null)
      onXong(T.saved)
    } catch (err) {
      onLoi(err)
    } finally {
      setDangLuu(false)
    }
  }

  async function batTat(serviceId: string, dung: boolean) {
    try {
      // B5: tắt dịch vụ đang bán cuối cùng → máy chủ hỏi lại; "Thôi" giữ nguyên công tắc (danh sách vẽ lại từ `ds` cũ).
      const kq = await hoi.chay((xn) => cauHinh.batTatDichVu(serviceId, dung, xn))
      if (kq.ket === 'luu') setDs(kq.gia.dichVu)
      else setDs((c) => (c === null ? c : [...c]))
    } catch (e) {
      onLoi(e)
    }
  }

  if (ds === null) return <KhungTai />
  const macDinhQuayLai = ds[0]?.quayLaiMacDinh ?? 28
  const hienForm = moForm || dangSua !== null || ds.length === 0

  return (
    <>
      {hoi.hop}
      {!hienForm && (
        <section className="the the-dem chi-dien-thoai">
          <button
            type="button"
            className="nut nut-chinh nut-rong"
            onClick={() => {
              setMoForm(true)
            }}
          >
            <Plus size={20} />
            {T.serviceAdd}
          </button>
        </section>
      )}
      <section className={`the the-dem ${hienForm ? '' : 'an-dien-thoai'}`}>
        <div className="the-tieu-de">
          <div>
            <h2>{dangSua === null ? T.serviceAddTitle : T.serviceEditTitle}</h2>
          </div>
          {dangSua !== null && (
            <button type="button" className="nut nut-nhat" onClick={() => moSua(null)}>
              {T.cancelEdit}
            </button>
          )}
          {dangSua === null && moForm && ds.length > 0 && (
            <button
              type="button"
              className="nut nut-nhat chi-dien-thoai"
              onClick={() => setMoForm(false)}
            >
              {T.close}
            </button>
          )}
        </div>
        <form className="form-them" onSubmit={(e) => void them(e)}>
          <div className="truong">
            <label className="nhan" htmlFor="dv-ten">
              {T.serviceNameLabel}
            </label>
            <input
              id="dv-ten"
              className="o-nhap"
              placeholder={T.serviceNameExample}
              value={ten}
              onChange={(e) => setTen(e.target.value)}
            />
          </div>
          <div className="truong">
            <label className="nhan" htmlFor="dv-gia">
              {T.servicePrice}
            </label>
            <input
              id="dv-gia"
              className="o-nhap"
              placeholder="150.000"
              value={gia}
              onChange={(e) => setGia(e.target.value)}
              inputMode="numeric"
            />
          </div>
          <div className="truong">
            <label className="nhan" htmlFor="dv-phut">
              {T.serviceMinutes}
            </label>
            <select
              id="dv-phut"
              className="o-chon"
              value={phut}
              onChange={(e) => setPhut(Number.parseInt(e.target.value, 10))}
            >
              {THOI_LUONG.map((p) => (
                <option key={p} value={p}>
                  {formatDuration(p)}
                </option>
              ))}
            </select>
          </div>
          <div className="truong truong-quay-lai">
            <label className="nhan" htmlFor="dv-quay-lai">
              {T.serviceReturn}
            </label>
            <select
              id="dv-quay-lai"
              className="o-chon"
              value={quayLai ?? ''}
              onChange={(e) =>
                setQuayLai(e.target.value === '' ? null : Number.parseInt(e.target.value, 10))
              }
            >
              <option value="">{T.serviceReturnDefault(macDinhQuayLai)}</option>
              {CHU_KY_QUAY_LAI.map((n) => (
                <option key={n} value={n}>
                  {T.serviceReturnDays(n)}
                </option>
              ))}
            </select>
          </div>
          <button
            type="submit"
            className="nut nut-chinh nut-form"
            disabled={dangLuu || ten.trim() === '' || giaSo === ''}
          >
            {dangSua === null && <Plus size={20} />}
            {dangSua === null ? T.serviceAdd : T.serviceSaveEdit}
          </button>
        </form>
        {giaSo !== '' && (
          <p className="goi-y-o" style={{ marginTop: 8 }}>
            {T.priceWillShow} <b>{tienVnd(giaSo)}</b>
          </p>
        )}
      </section>

      <section className="the the-dem">
        <div className="the-tieu-de">
          <div>
            <h2>{T.serviceListTitle}</h2>
            <p>{T.serviceListHint}</p>
          </div>
          <span className="nhan-nho">{T.countItems(ds.length)}</span>
        </div>
        {ds.length === 0 ? (
          <Rong icon={<BieuTuong size={28} />} tieuDe={T.serviceEmptyTitle} moTa={T.serviceEmpty} />
        ) : (
          <ul className="ds-muc">
            {ds.map((d) => (
              <li key={d.variantId} className={`muc ${d.dangDung ? '' : 'mo-di'}`}>
                <div className="chu">
                  <b>{d.ten}</b>
                  <span>{formatDuration(d.phut)}</span>
                </div>
                <span className="gia">{tienVnd(d.giaVnd)}</span>
                <CongTac
                  bat={d.dangDung}
                  nhan={
                    <span className="nhan-cong-tac">
                      {d.dangDung ? T.serviceOnSale : T.serviceOffSale}
                    </span>
                  }
                  onDoi={(bat) => void batTat(d.serviceId, bat)}
                />
                <button type="button" className="nut nut-vien nut-nho" onClick={() => moSua(d)}>
                  {T.edit}
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  )
}

// =============================================================================== Nhân viên
function TheNhanVien({
  onLoi,
  onXong,
  laChuTiem,
  onTaoChiNhanh,
  nhapDau,
  onDaDungNhap,
  onXemTrenLich,
  onMoChiNhanh,
  toiId,
}: ChungProps & {
  onXemTrenLich?: (ngay: string, chiNhanhId: string) => void
  onMoChiNhanh?: () => void
  /** Người đang đăng nhập (v0.22.3, Q3): tờ nhân viên của CHÍNH MÌNH khác tờ của người khác. */
  toiId: string
  laChuTiem: boolean
  onTaoChiNhanh: (nhap: { ten: string; sdt: string }) => void
  /** Tên + số đã gõ trước khi sang tạo chi nhánh: có thì mở lại tờ "Thêm nhân viên" với đúng chữ đó. */
  nhapDau: { ten: string; sdt: string } | null
  onDaDungNhap: () => void
}) {
  const [ds, setDs] = useState<NhanVienCH[] | null>(null)
  const [chiNhanh, setChiNhanh] = useState<ChiNhanhCH[]>([])
  const [dichVu, setDichVu] = useState<DichVuCH[]>([])
  const nhapDauRef = useRef(nhapDau)
  const [dangSua, setDangSua] = useState<NhanVienCH | 'moi' | null>(nhapDau === null ? null : 'moi')
  // Đã dùng bản nháp: gỡ khỏi bộ nhớ của màn Cấu hình (chỉ dùng một lần).
  // biome-ignore lint/correctness/useExhaustiveDependencies: chạy đúng một lần khi thẻ mở.
  useEffect(() => {
    if (nhapDauRef.current !== null) onDaDungNhap()
  }, [])

  const napHet = useCallback(async () => {
    try {
      const [a, b, c] = await Promise.all([
        cauHinh.nhanVien(),
        cauHinh.chiNhanh(),
        cauHinh.dichVu(),
      ])
      setDs(a.nhanVien)
      setChiNhanh(b.chiNhanh)
      setDichVu(c.dichVu.filter((x) => x.dangDung))
      return a.nhanVien
    } catch (e) {
      onLoi(e)
      return null
    }
  }, [onLoi])

  useEffect(() => {
    void napHet()
  }, [napHet])

  if (ds === null) return <KhungTai />

  return (
    <>
      <section className="the the-dem">
        <div className="the-tieu-de">
          <div>
            <h2>{T.staffListTitle}</h2>
            <p>{laChuTiem ? T.staffListHint : T.staffShiftOnlyNote}</p>
          </div>
          {laChuTiem && (
            <button type="button" className="nut nut-chinh" onClick={() => setDangSua('moi')}>
              <UserPlus size={18} />
              {T.staffAdd}
            </button>
          )}
        </div>
        {ds.length === 0 ? (
          <Rong icon={<Users size={28} />} tieuDe={T.staffEmptyTitle} moTa={T.staffEmptyHint} />
        ) : (
          <ul className="ds-muc">
            {ds.map((nv) => (
              <li key={nv.id} className={`muc ${nv.dangLam ? '' : 'mo-di'}`}>
                <Avatar ten={nv.ten} />
                <div className="chu">
                  <b>{nv.ten}</b>
                  <span>
                    <span className="nhan-nho">{tenVaiHienThi(nv.vai, nv.tenVai)}</span>
                    {/* Một dòng phụ duy nhất: các dòng nhân viên cao bằng nhau (vòng 3). */}
                    <CacCum
                      cac={[
                        nv.sdt === null ? null : hienSdt(nv.sdt),
                        nv.bienTheIds.length > 0 ? T.doesServices(nv.bienTheIds.length) : null,
                      ]}
                    />
                    {/* Tình trạng mã đăng nhập: chỉ chủ tiệm thấy (Quản lý nhận bản rút gọn). */}
                    {laChuTiem && !nv.coMaDangNhap && (
                      <span className="nhan-nho canh-bao">{T.staffNoPin}</span>
                    )}
                    {laChuTiem && nv.phaiDoiMa && (
                      <span className="nhan-nho">{T.staffMustChange}</span>
                    )}
                    {!nv.dangLam && <span className="nhan-nho">{T.staffStopped}</span>}
                  </span>
                </div>
                <button type="button" className="nut nut-vien" onClick={() => setDangSua(nv)}>
                  {T.edit}
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {dangSua !== null && (
        <ToNhanVien
          nv={dangSua === 'moi' ? null : dangSua}
          nhapDau={dangSua === 'moi' ? nhapDauRef.current : null}
          chiNhanh={chiNhanh}
          dichVu={dichVu}
          laChuTiem={laChuTiem}
          toiId={toiId}
          onDong={() => setDangSua(null)}
          onTaoChiNhanh={onTaoChiNhanh}
          {...(onXemTrenLich === undefined ? {} : { onXemTrenLich })}
          {...(onMoChiNhanh === undefined ? {} : { onMoChiNhanh })}
          onLoi={onLoi}
          onDaLuu={async (id, cau) => {
            const moi = await napHet()
            const nv = moi?.find((x) => x.id === id)
            if (nv !== undefined) setDangSua(nv)
            onXong(cau)
          }}
        />
      )}
    </>
  )
}

function ToNhanVien({
  nv,
  nhapDau,
  chiNhanh,
  dichVu,
  laChuTiem,
  onDong,
  onTaoChiNhanh,
  onXemTrenLich,
  onMoChiNhanh,
  toiId,
  onLoi,
  onDaLuu,
}: {
  onXemTrenLich?: (ngay: string, chiNhanhId: string) => void
  /** v0.22.3 B1: sang Cấu hình → Chi nhánh (Ngày tiệm nghỉ) khi người này là người nhận khách cuối cùng. */
  onMoChiNhanh?: () => void
  /** Người đang đăng nhập: tờ của CHÍNH MÌNH khác tờ của người khác (v0.22.3, Q3). */
  toiId: string
  nv: NhanVienCH | null
  /** Người MỚI: tên + số đã gõ trước khi phải sang tạo chi nhánh. */
  nhapDau: { ten: string; sdt: string } | null
  chiNhanh: ChiNhanhCH[]
  dichVu: DichVuCH[]
  laChuTiem: boolean
  onDong: () => void
  /** Tiệm chưa có chi nhánh: dẫn sang tạo chi nhánh (đóng tờ này, mở thẻ Chi nhánh), mang theo chữ đã gõ. */
  onTaoChiNhanh: (nhap: { ten: string; sdt: string }) => void
  onLoi: (e: unknown) => void
  onDaLuu: (id: string, cau: string) => Promise<void>
}) {
  const hoi = useHoiNgungNhan()
  /** v0.23.1 (ADR-123): hộp "Nhập mã của bạn" — Đặt mã / Đăng xuất / Cho nghỉ / Đi làm lại của một chủ tiệm. */
  const hoiMa = useHoiMaCuaToi()
  const viec = viecTrenTo(nv?.id ?? null, toiId)
  /** B4: mã đăng nhập gõ lại khi đổi SỐ ĐĂNG NHẬP của chính mình; `loiMa` = câu máy chủ chê mã ngay tại ô. */
  const [maXacNhan, setMaXacNhan] = useState('')
  const [loiMa, setLoiMa] = useState<string | null>(null)
  /** B-L2: máy chủ đã đòi mã (theo số đăng nhập) dù web thấy số không đổi → ô mã phải hiện. */
  const [mayChuDoi, setMayChuDoi] = useState(false)
  /** B3: "Đổi mã của tôi" gập sau một nút (ẩn mặc định). */
  const [moDoiMa, setMoDoiMa] = useState(false)
  const [daDoiMa, setDaDoiMa] = useState(false)
  const [ten, setTen] = useState(nv?.ten ?? nhapDau?.ten ?? '')
  const [sdt, setSdt] = useState(nv === null ? (nhapDau?.sdt ?? '') : hienSdt(nv.sdt))
  const [vai, setVai] = useState(nv?.vai ?? 'technician')
  /*
   * ADR-074: Quản lý / Lễ tân chỉ GIAO được khi "Phân quyền chi tiết" bật. Trước đây tắt thì hai vai này biến
   * khỏi ô chọn mà không nói gì (phản ánh 30/09: "chỉ thấy Chủ tiệm và Kỹ thuật viên"). Nay luôn hiện đủ 4 vai;
   * chọn vai cần công tắc mà công tắc đang tắt → nói rõ và cho bật ngay một chạm.
   */
  const coVaiChiTiet = useTinhNang('phan-quyen')
  const vaiHien = VAI_CHON.map((v) => ({ ...v, ten: tenVaiHienThi(v.ma, v.ten) }))
  const [dangBatVai, setDangBatVai] = useState(false)
  async function batPhanQuyen() {
    setDangBatVai(true)
    try {
      datCaiDat(await caiDatApi.luu({ tinhNang: { 'phan-quyen': true } }))
    } catch (e) {
      onLoi(e)
    } finally {
      setDangBatVai(false)
    }
  }
  /*
   * "Người này nhận khách" (phản ánh 25/09): ai cũng có thể làm dịch vụ — chủ tiệm tự làm nail vẫn
   * phải có ca + dịch vụ để khách đặt được. Trước đây chỉ vai "Kỹ thuật viên" có hai mục này, và đổi
   * vai là xoá sạch dịch vụ đã gán.
   */
  const [nhanKhach, setNhanKhach] = useState(
    nv === null ? true : nv.vai === 'technician' || nv.bienTheIds.length > 0,
  )
  const [moBangVai, setMoBangVai] = useState(false)
  // ĐIỀN SẴN thứ đang giao: lưu là thay toàn bộ (cau-hinh.int.test.ts ca 4b).
  const [cnChon, setCnChon] = useState<string[]>(
    nv?.chiNhanhIds ?? (chiNhanh[0] ? [chiNhanh[0].id] : []),
  )
  const [dvChon, setDvChon] = useState<string[]>(nv?.bienTheIds ?? dichVu.map((d) => d.variantId))
  /*
   * Ca mặc định của người mới = giờ mở cửa của chi nhánh đang chọn (kế hoạch 10/2026, 1.4a) — trước đây cứng
   * T2–T7 nên Chủ nhật "mở cửa mà không ai làm". Đổi chi nhánh (người mới) thì ca đi theo, cho tới khi chủ tiệm
   * tự sửa ca một lần.
   */
  const gioLamCua = (cnId: string | undefined) => chiNhanh.find((c) => c.id === cnId)?.gioLam ?? []
  const [ca, setCa] = useState<Record<number, { tu: number; den: number; lam: boolean }>>(() =>
    caMacDinhTuGioLam(gioLamCua(cnChon[0])),
  )
  const caDaSua = useRef(false)
  const suaCa = (g: Record<number, { tu: number; den: number; lam: boolean }>) => {
    caDaSua.current = true
    setCa(g)
  }
  const cnDauTien = cnChon[0]
  // biome-ignore lint/correctness/useExhaustiveDependencies: chỉ theo chi nhánh đang chọn của người MỚI; `chiNhanh` đã nạp xong trước khi mở tờ.
  useEffect(() => {
    if (nv !== null || caDaSua.current) return
    setCa(caMacDinhTuGioLam(gioLamCua(cnDauTien)))
  }, [cnDauTien])
  const [maMoi, setMaMoi] = useState<{ ten: string; ma: string } | null>(null)
  // Mã QR trang chủ (chỉ địa chỉ SAPA) mở ngay tại mục đặt mã — không tờ chồng tờ (nhóm C, ADR-114).
  const [hienQr, setHienQr] = useState(false)
  const [dangLuu, setDangLuu] = useState(false)
  /*
   * Ca của nhân viên CÓ SẴN chỉ được lưu sau khi đã tải mẫu ca cũ về. Bấm Lưu trước lúc đó mà vẫn
   * gửi ca thì ca thật bị ghi đè bằng ca mặc định — cùng họ lỗi "form rỗng xoá dữ liệu thật".
   */
  const [caDaNap, setCaDaNap] = useState(nv === null)
  /** Chi nhánh của mẫu ca đang có — Quản lý lưu ca giữ đúng chi nhánh này (soát bảo mật 25/09). */
  const [cnCuaCa, setCnCuaCa] = useState<string | null>(null)

  useEffect(() => {
    if (nv === null) return
    void cauHinh
      .mauCa(nv.id)
      .then((r) => {
        setCaDaNap(true)
        setCnCuaCa(r.branchId)
        if (r.mau.length === 0) return
        const g: Record<number, { tu: number; den: number; lam: boolean }> = {}
        for (const t of CAC_THU) g[t] = { tu: 540, den: 1200, lam: false }
        for (const m of r.mau) g[m.thu] = { tu: m.batDauMin, den: m.ketThucMin, lam: true }
        setCa(g)
      })
      .catch(onLoi)
  }, [nv, onLoi])

  /*
   * Người MỚI đã lưu được hồ sơ mà bước xếp ca lỗi → bấm Lưu lần nữa phải SỬA người đó, không tạo thêm một
   * người trùng số (trước đây lần hai nổ "Hệ thống đang lỗi" vì trùng số điện thoại — phản ánh 30/09).
   */
  const idDaTao = useRef<string | null>(nv?.id ?? null)

  /*
   * BK-2 (ADR-096): đổi ca / cho nghỉ việc đã LƯU nhưng còn lịch không có người làm → liệt kê ngay trong tờ (tờ không
   * đóng sau khi lưu). Cuộn tới cảnh báo vì nút "Cho nghỉ" nằm cuối tờ, cảnh báo ở đầu.
   */
  const [vuong, setVuong] = useState<{ loiDau: string; ds: LichVuongNghi[]; tong: number } | null>(
    null,
  )
  const hoCanhBao = useRef<HTMLDivElement | null>(null)
  // biome-ignore lint/correctness/useExhaustiveDependencies: chỉ cuộn khi có cảnh báo mới.
  useEffect(() => {
    if (vuong !== null) hoCanhBao.current?.scrollIntoView?.({ block: 'start' })
  }, [vuong])
  function ghiVuong(loiDau: string, r: { soLichVuong?: number; lichVuong?: LichVuongNghi[] }) {
    const ds = r.lichVuong ?? []
    setVuong(ds.length > 0 ? { loiDau, ds, tong: r.soLichVuong ?? ds.length } : null)
  }

  async function luu() {
    // 1.4b: người nhận khách cần ca mà tiệm chưa có chi nhánh → không lưu im lặng thiếu ca (nút Lưu đã được thay,
    // đây là rào thứ hai).
    if (canTaoChiNhanhTruoc({ nhanKhach, soChiNhanh: chiNhanh.length })) {
      onTaoChiNhanh({ ten, sdt })
      return
    }
    setDangLuu(true)
    setLoiMa(null)
    try {
      // B5: bỏ "Người này nhận khách" của người cuối → máy chủ hỏi lại; "Thôi, để như cũ" = không lưu gì.
      const kq = await hoi.chay((xn) =>
        cauHinh.luuNhanVien(
          {
            ...(idDaTao.current === null ? {} : { id: idDaTao.current }),
            ten: ten.trim(),
            sdt: sdt.trim() === '' ? null : sdt.trim(),
            vai,
            chiNhanhIds: cnChon,
            bienTheIds: nhanKhach ? dvChon : [],
            // B4: đổi số đăng nhập của chính mình — mã gõ lại (máy chủ đếm lượt như đăng nhập).
            ...(canMa ? { maXacNhan } : {}),
          },
          xn,
        ),
      )
      if (kq.ket === 'thoi') return
      // B4: số mới đã lưu → máy nhớ số mới (lần hết phiên sau màn đăng nhập điền đúng số), và nói rõ cho chủ tiệm.
      const soMoi = canMa ? chuanHoaSdtVn(sdt.trim()) : null
      if (soMoi !== null) {
        nhoSoMoi(soMoi)
        setMaXacNhan('')
      }
      const r = kq.gia
      idDaTao.current = r.id
      // Người nhận khách: xếp luôn ca (mặc định với người mới), để khách đặt được ngay (học Fresha).
      const cn = cnChon[0]
      if (nhanKhach && cn !== undefined && caDaNap) {
        const kc = await hoi.chay((xn) => cauHinh.luuMauCa(r.id, cn, mauTuForm(), xn))
        // "Thôi" ở bước ca: hồ sơ đã lưu, ca giữ như cũ (người nhận khách cuối cùng không bị bỏ trống ca).
        if (kc.ket === 'thoi') return
        ghiVuong(T.shiftSavedBusy, kc.gia)
      }
      await onDaLuu(r.id, soMoi === null ? T.saved : T.staffPhoneSelfDone(hienSdt(soMoi)))
    } catch (e) {
      // Mã đăng nhập thiếu / sai / đang khoá: nói ngay tại ô mã (tờ đang mở che thanh lỗi ở đầu trang).
      if (e instanceof ApiFailure && mayChuDoiMa(e)) {
        setMayChuDoi(true)
        setLoiMa(e.messageVi)
      } else onLoi(e)
    } finally {
      setDangLuu(false)
    }
  }

  /** Quản lý (quyền `xep-ca`): chỉ lưu ca — hồ sơ người làm là việc của chủ tiệm. */
  async function luuCa() {
    const cn = cnCuaCa ?? cnChon[0]
    if (nv === null || cn === undefined || !caDaNap) return
    setDangLuu(true)
    try {
      const kc = await hoi.chay((xn) => cauHinh.luuMauCa(nv.id, cn, mauTuForm(), xn))
      if (kc.ket === 'thoi') return
      ghiVuong(T.shiftSavedBusy, kc.gia)
      await onDaLuu(nv.id, T.saved)
    } catch (e) {
      onLoi(e)
    } finally {
      setDangLuu(false)
    }
  }

  function mauTuForm(): MauCaCH[] {
    return CAC_THU.filter((t) => ca[t]?.lam).map((t) => ({
      thu: t,
      batDauMin: ca[t]?.tu ?? 540,
      ketThucMin: ca[t]?.den ?? 1200,
    }))
  }

  /*
   * v0.23.1 (ADR-123; B-N1, B-L3): ba nút dưới đây đụng tới phiên / mã / quyền của người khác. Với nhân viên thường như cũ (một chạm);
   * với một CHỦ TIỆM khác (hoặc "Đăng xuất các máy khác" của chính mình) máy chủ trả `canMa` → hộp "Nhập mã của bạn" → gọi lại kèm mã.
   * Giao diện không đoán trước việc nào cần mã: `hoiMa.chay` chỉ mở hộp khi máy chủ đòi.
   */
  async function datMa() {
    if (nv === null) return
    const so = sdt.trim() === '' ? (nv.sdt ?? '') : sdt.trim()
    if (so === '') {
      onLoi(new ApiFailure(400, { code: 'VALIDATION', message: T.phoneInvalid }))
      return
    }
    try {
      const kq = await hoiMa.chay((ma) => cauHinh.datMa(nv.id, so, ma))
      if (kq.ket === 'thoi') return
      setMaMoi({ ten: kq.gia.ten, ma: kq.gia.ma })
    } catch (e) {
      onLoi(e)
    }
  }

  const [daThoat, setDaThoat] = useState<number | null>(null)
  async function dangXuatHet() {
    if (nv === null) return
    try {
      const kq = await hoiMa.chay((ma) => cauHinh.dangXuatHet(nv.id, ma))
      if (kq.ket === 'thoi') return
      setDaThoat(kq.gia.soPhien)
    } catch (e) {
      onLoi(e)
    }
  }

  async function batTat(dangLam: boolean) {
    if (nv === null) return
    try {
      // B5: cho nghỉ việc người nhận khách cuối cùng → máy chủ hỏi lại (bật lại thì không bao giờ hỏi). Hai lớp: mã của mình (chủ khác) → hỏi ngừng nhận khách.
      const kq = await hoiMa.chay((ma) =>
        hoi.chay((xn) => cauHinh.batTatNhanVien(nv.id, dangLam, xn, ma)),
      )
      if (kq.ket === 'thoi' || kq.gia.ket === 'thoi') return
      ghiVuong(T.stopSavedBusy, kq.gia.gia)
      await onDaLuu(nv.id, T.saved)
    } catch (e) {
      onLoi(e)
    }
  }

  const coCa = nhanKhach
  /*
   * Nút Lưu chỉ hiện khi đủ thông tin (phản ánh 30/09): thiếu gì thì nói thiếu gì, không để bấm rồi nhận
   * "Hệ thống đang lỗi". Máy chủ vẫn kiểm lại từng điều (nhan-vien-form.int.test.ts).
   */
  const vaiCanCongTac = (vai === 'manager' || vai === 'cashier') && vai !== nv?.vai
  const sdtSai = sdt.trim() !== '' && chuanHoaSdtVn(sdt.trim()) === null
  /** Người này nhận khách mà tiệm chưa có chi nhánh nào: phải tạo chi nhánh trước (hộp cảnh báo ở chân tờ). */
  const canTaoCN =
    laChuTiem && canTaoChiNhanhTruoc({ nhanKhach: coCa, soChiNhanh: chiNhanh.length })
  // B4: đổi số đăng nhập CỦA CHÍNH MÌNH sang số khác → phải gõ mã đăng nhập để xác nhận.
  // v0.23.1 (ADR-123): và khi việc chạm tới một CHỦ TIỆM — giao / bỏ vai Chủ tiệm, đổi số của chủ khác đã có mã — cùng ô, mã của MÌNH.
  const doiSoCuaToi = canMaKhiDoiSo({
    laToi: viec.laToi,
    sdtNhap: sdt,
    sdtHienTai: nv?.sdt ?? null,
  })
  const canMaTinh = canMaKhiLuu({
    laToi: viec.laToi,
    laMoi: nv === null,
    vaiCu: nv?.vai ?? null,
    vaiChon: vai,
    sdtNhap: sdt,
    sdtHienTai: nv?.sdt ?? null,
  })
  const canMa = hienOMaXacNhan({ canMaTinh, mayChuDoi })
  // G-L1: gợi ý dưới nhãn ô mã nói đúng việc (đổi số của chủ khác ≠ "đổi quyền Chủ tiệm").
  const viecMa = viecOMa({
    laToi: viec.laToi,
    laMoi: nv === null,
    vaiCu: nv?.vai ?? null,
    vaiChon: vai,
    sdtNhap: sdt,
    sdtHienTai: nv?.sdt ?? null,
  })
  const goiYMa =
    viecMa === 'doi-so-cua-toi'
      ? T.staffPhoneNeedPin
      : viecMa === 'doi-so-chu'
        ? T.staffOtherPhonePinHint
        : T.staffOwnerPinHint
  const thieu: string[] = []
  if (ten.trim() === '') thieu.push(T.staffMissingName)
  if (sdtSai) thieu.push(T.staffMissingPhone)
  if (canMa && maXacNhan === '') {
    thieu.push(doiSoCuaToi ? T.staffMissingPin : T.staffMissingPinOwner)
  }
  if (vaiCanCongTac && !coVaiChiTiet) thieu.push(T.staffMissingRoleSwitch)
  if (chiNhanh.length > 0 && cnChon.length === 0) thieu.push(T.staffMissingBranch)
  if (coCa && dichVu.length > 0 && dvChon.length === 0) thieu.push(T.staffMissingService)
  return (
    <>
      <To nhan={T.staffEditTitle} onDong={onDong}>
        <header className="to-dau">
          {nv !== null && <Avatar ten={nv.ten} />}
          <h2>{nv === null ? T.staffNew : nv.ten}</h2>
          <button
            type="button"
            className="nut nut-nhat nut-icon"
            aria-label={T.close}
            onClick={onDong}
          >
            <X size={22} />
          </button>
        </header>
        <div className="to-than">
          <div ref={hoCanhBao}>
            {vuong !== null && (
              <CanhBaoLichVuong
                loiDau={vuong.loiDau}
                ds={vuong.ds}
                tong={vuong.tong}
                {...(onXemTrenLich === undefined
                  ? {}
                  : {
                      onMo: (v: LichVuongNghi) => onXemTrenLich(v.batDau.slice(0, 10), v.branchId),
                    })}
              />
            )}
          </div>
          {!laChuTiem && nv !== null && (
            <div className="muc-to">
              <p className="chu-phu">
                {tenVaiHienThi(nv.vai, nv.tenVai)}
                {nv.sdt !== null && ` · ${hienSdt(nv.sdt)}`}
              </p>
              <p className="chu-phu">{T.staffShiftOnlyNote}</p>
            </div>
          )}
          {laChuTiem && (
            <>
              <div className="muc-to" style={{ display: 'grid', gap: 14 }}>
                <h3>{T.staffEditTitle}</h3>
                <div className="truong">
                  <label className="nhan" htmlFor="nv-ten">
                    {T.staffNameLabel}
                  </label>
                  <input
                    id="nv-ten"
                    className="o-nhap"
                    value={ten}
                    placeholder={T.nameExample}
                    onChange={(e) => setTen(e.target.value)}
                  />
                </div>
                <div className="truong">
                  <label className="nhan" htmlFor="nv-sdt">
                    {T.staffPhone}
                  </label>
                  <input
                    id="nv-sdt"
                    className="o-nhap"
                    value={sdt}
                    placeholder={T.phonePlaceholder}
                    onChange={(e) => setSdt(e.target.value)}
                    inputMode="tel"
                    aria-invalid={sdtSai}
                  />
                  {sdtSai && sdt.replace(/\D/g, '').length >= 9 && (
                    <p className="chu-loi">{T.phoneInvalid}</p>
                  )}
                </div>
                {/* B4: đổi SỐ ĐĂNG NHẬP của chính mình sang số khác → gõ mã đăng nhập để xác nhận (ẩn cho tới khi số đổi). */}
                {canMa && (
                  <div className="truong">
                    <label className="nhan" htmlFor="nv-ma-xac-nhan">
                      {T.staffPhonePinLabel}
                    </label>
                    <p className="chu-phu">{goiYMa}</p>
                    <div className="o-nhap-icon">
                      <KeyRound size={20} />
                      <input
                        id="nv-ma-xac-nhan"
                        className="o-nhap"
                        type="password"
                        value={maXacNhan}
                        placeholder={T.pinPlaceholder}
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        aria-invalid={loiMa !== null}
                        onChange={(e) => {
                          setMaXacNhan(e.target.value)
                          setLoiMa(null)
                        }}
                      />
                    </div>
                    {loiMa !== null && (
                      <p className="chu-loi" role="alert">
                        {loiMa}
                      </p>
                    )}
                  </div>
                )}
                <div className="truong">
                  <span className="nhan">{T.staffRole}</span>
                  <div className="hang-chip">
                    {vaiHien.map((v) => (
                      <button
                        key={v.ma}
                        type="button"
                        aria-pressed={vai === v.ma}
                        // B7: tờ của chính mình — máy chủ luôn từ chối tự đổi vai nên khoá chip, không đưa nút làm không được.
                        disabled={viec.khoaVai}
                        className={`chip ${vai === v.ma ? 'dang-chon' : ''}`}
                        onClick={() => {
                          setVai(v.ma)
                          if (v.ma === 'technician') setNhanKhach(true)
                        }}
                      >
                        {vai === v.ma && <Check size={16} />}
                        {v.ten}
                      </button>
                    ))}
                  </div>
                  <p className="chu-phu">{viec.khoaVai ? T.staffRoleSelfNote : T.roleHint[vai]}</p>
                  {vaiCanCongTac && !coVaiChiTiet && (
                    <div className="muc-to">
                      <p className="chu-phu">{T.roleNeedSwitch}</p>
                      <button
                        type="button"
                        className="nut nut-vien"
                        disabled={dangBatVai}
                        onClick={() => void batPhanQuyen()}
                      >
                        {T.roleNeedSwitchBtn}
                      </button>
                    </div>
                  )}
                  <button
                    type="button"
                    className="nut-lien-ket"
                    aria-expanded={moBangVai}
                    onClick={() => setMoBangVai((x) => !x)}
                  >
                    {moBangVai ? T.roleTableClose : T.roleTableOpen}
                  </button>
                  {moBangVai && <BangVai onLoi={onLoi} />}
                </div>
                {vai !== 'technician' && (
                  <CongTac
                    bat={nhanKhach}
                    nhan={
                      <span>
                        <b>{T.staffServesCustomers}</b>
                        <br />
                        <span className="chu-phu">{T.staffServesHint}</span>
                      </span>
                    }
                    onDoi={setNhanKhach}
                  />
                )}
              </div>

              {/* Chưa có chi nhánh thì không có gì để chọn: ẩn cả mục, lời nhắn nằm ở chân tờ (vòng 33 B1). */}
              {chiNhanh.length > 0 && (
                <div className="muc-to">
                  <h3>{T.staffBranches}</h3>
                  <div className="hang-chip">
                    {chiNhanh.map((cn) => (
                      <button
                        key={cn.id}
                        type="button"
                        className={`chip ${cnChon.includes(cn.id) ? 'dang-chon' : ''}`}
                        onClick={() => setCnChon(cnChon.includes(cn.id) ? [] : [cn.id])}
                      >
                        {cnChon.includes(cn.id) && <Check size={16} />}
                        {cn.ten}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {laChuTiem && coCa && (
            <div className="muc-to">
              <h3>
                {T.staffServices} · {dvChon.length}/{dichVu.length}
              </h3>
              <div className="hang-chip">
                {dichVu.map((d) => {
                  const co = dvChon.includes(d.variantId)
                  return (
                    <button
                      key={d.variantId}
                      type="button"
                      className={`chip ${co ? 'dang-chon' : ''}`}
                      aria-pressed={co}
                      onClick={() =>
                        setDvChon(
                          co ? dvChon.filter((x) => x !== d.variantId) : [...dvChon, d.variantId],
                        )
                      }
                    >
                      {co && <Check size={16} />}
                      {d.ten}
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          {coCa && (
            <div className="muc-to">
              <h3>{T.shiftTitle}</h3>
              <div className="ds-thu">
                {CAC_THU.map((t) => {
                  const g = ca[t] ?? { tu: 540, den: 1200, lam: false }
                  return (
                    <div key={t} className="hang-thu hep">
                      <CongTac
                        bat={g.lam}
                        nhan={<b>{THU_VN[t]}</b>}
                        onDoi={(bat) => suaCa({ ...ca, [t]: { ...g, lam: bat } })}
                      />
                      {g.lam ? (
                        <span className="khoang">
                          <ChonGio
                            phut={g.tu}
                            nhan={`${THU_VN[t]} · ${T.shiftFrom}`}
                            onDoi={(p) => suaCa({ ...ca, [t]: { ...g, tu: p } })}
                          />
                          <span className="gach">–</span>
                          <ChonGio
                            phut={g.den}
                            nhan={`${THU_VN[t]} · ${T.shiftTo}`}
                            onDoi={(p) => suaCa({ ...ca, [t]: { ...g, den: p } })}
                          />
                        </span>
                      ) : (
                        <span className="nghi">{T.hoursClosedDay}</span>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {nv !== null && coCa && (
            <MucNgayNghi
              staffId={nv.id}
              onLoi={onLoi}
              {...(onMoChiNhanh === undefined ? {} : { onMoChiNhanh })}
            />
          )}

          {nv !== null && laChuTiem && (
            <div className="muc-to">
              <h3>{viec.datMaHo ? T.staffSetPin : T.staffPinSection}</h3>
              {/* B3: mã của chính mình KHÔNG đặt hộ được (lối vượt bước gõ mã cũ) — tự đổi, cần mã đang dùng, máy đang dùng không bị đăng xuất. */}
              {viec.doiMaCuaToi &&
                (moDoiMa ? (
                  <FormDoiMa kieuNut="phu" onXong={() => setDaDoiMa(true)} />
                ) : (
                  <button
                    type="button"
                    className="nut nut-vien"
                    aria-expanded={moDoiMa}
                    onClick={() => {
                      setDaDoiMa(false)
                      setMoDoiMa(true)
                    }}
                  >
                    <KeyRound size={18} />
                    {T.staffChangeMyPin}
                  </button>
                ))}
              {viec.doiMaCuaToi && daDoiMa && <p className="chu-phu">{T.staffChangeMyPinDone}</p>}
              {viec.datMaHo &&
                (maMoi !== null ? (
                  <div className="ma-moi">
                    <span>
                      {T.staffPinIs} {maMoi.ten}
                    </span>
                    <b>{maMoi.ma}</b>
                    <span className="chu-phu">{T.staffPinNote}</span>
                  </div>
                ) : (
                  <button type="button" className="nut nut-vien" onClick={() => void datMa()}>
                    <KeyRound size={18} />
                    {T.staffSetPin}
                  </button>
                ))}
              <button
                type="button"
                className="nut nut-vien"
                aria-expanded={hienQr}
                aria-controls="nv-qr-sapa"
                onClick={() => setHienQr((v) => !v)}
              >
                <QrCode size={18} />
                {hienQr ? T.staffQrAn : T.staffQrMo}
              </button>
              {hienQr && (
                <div id="nv-qr-sapa" className="nv-qr-sapa">
                  <AnhQrSapa co={200} />
                  <p className="chu-phu">{T.staffQrGoiY}</p>
                </div>
              )}
              {/* Mất điện thoại / cho nghỉ việc: mọi máy đang đăng nhập của người này thoát ngay. */}
              <button type="button" className="nut nut-vien" onClick={() => void dangXuatHet()}>
                <LogOut size={18} />
                {viec.laToi ? T.staffLogoutOthers : T.staffLogoutAll}
              </button>
              {daThoat !== null && (
                <p className="chu-phu">
                  {viec.laToi ? T.staffLogoutOthersDone(daThoat) : T.staffLogoutDone(daThoat)}
                </p>
              )}
            </div>
          )}

          {/* B7: tờ của chính mình không có "Cho nghỉ việc" (máy chủ luôn từ chối tự nghỉ việc). */}
          {nv !== null && laChuTiem && viec.choNghiViec && (
            <div className="muc-to">
              <h3>{T.staffDangerZone}</h3>
              {nv.dangLam ? (
                <button type="button" className="nut nut-nguy" onClick={() => void batTat(false)}>
                  <Trash2 size={18} />
                  {T.staffStop}
                </button>
              ) : (
                <button type="button" className="nut nut-vien" onClick={() => void batTat(true)}>
                  {T.staffResume}
                </button>
              )}
            </div>
          )}
        </div>
        <footer className={`to-chan ${canTaoCN ? 'to-chan-nhieu-hang' : ''}`}>
          {/* Cảnh báo chưa có chi nhánh: hộp riêng chiếm hết hàng ngay trên chân (vòng 33 B1). */}
          {canTaoCN && (
            <ThongBao loai="canh-bao" lon>
              {T.staffNoBranch}
            </ThongBao>
          )}
          <button type="button" className="nut nut-vien nut-lon" onClick={onDong}>
            {T.close}
          </button>
          {laChuTiem ? (
            canTaoCN ? (
              <button
                type="button"
                className="nut nut-chinh nut-lon"
                onClick={() => onTaoChiNhanh({ ten, sdt })}
              >
                {T.staffNoBranchGo}
              </button>
            ) : thieu.length === 0 ? (
              <button
                type="button"
                className="nut nut-chinh nut-lon"
                disabled={dangLuu}
                onClick={() => void luu()}
              >
                {dangLuu ? T.savingText : T.staffSave}
              </button>
            ) : (
              <p className="chu-phu" role="status">
                {T.staffMissingPrefix} {thieu.join(', ')}.
              </p>
            )
          ) : (
            coCa && (
              <button
                type="button"
                className="nut nut-chinh nut-lon"
                disabled={dangLuu || !caDaNap}
                onClick={() => void luuCa()}
              >
                {dangLuu ? T.savingText : T.staffShiftSave}
              </button>
            )
          )}
        </footer>
      </To>
      {/* Tờ hỏi lại đứng NGANG tờ nhân viên (không lồng trong nó): `lop-phu` fixed không bị nhốt bởi tờ cha. */}
      {/* Hộp mã vẽ TRƯỚC: khi lưu xong lớp mã mà máy chủ còn hỏi "không còn ai nhận khách", tờ hỏi đó nằm TRÊN hộp mã (đang "Đang kiểm…"). */}
      {hoiMa.hop}
      {hoi.hop}
    </>
  )
}

/**
 * "Ai làm được gì?" — bảng 4 vai × việc chính, đọc từ bảng quyền của MÁY CHỦ (`/v1/admin/vai`) theo
 * công tắc hiện tại của tiệm. Không có bản gõ tay thứ hai để lệch với thứ máy chủ thật sự cho phép.
 */
function BangVai({ onLoi }: { onLoi: (e: unknown) => void }) {
  const [ds, setDs] = useState<
    { ma: string; ten: string; quyen: string[]; chiCuaMinh: string[] }[] | null
  >(null)
  useEffect(() => {
    void cauHinh
      .vai()
      .then((r) => setDs(r.vai))
      .catch(onLoi)
  }, [onLoi])
  if (ds === null) return <Xuong cao={120} />
  return (
    <div className="bang-vai-khung">
      <table className="bang-vai">
        <caption>{T.roleTableTitle}</caption>
        <thead>
          <tr>
            <td />
            {ds.map((v) => (
              <th key={v.ma} scope="col">
                {v.ten}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {T.roleJobs.map(([q, ten]) => (
            <tr key={q}>
              <th scope="row">{ten}</th>
              {ds.map((v) => (
                <td key={v.ma}>
                  {!v.quyen.includes(q) ? (
                    <span className="khong">—</span>
                  ) : v.chiCuaMinh.includes(q) ? (
                    <span className="co-rieng">{T.roleTableOwn}</span>
                  ) : (
                    <Check size={16} className="co" />
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <p className="chu-phu">{T.roleTableNote}</p>
    </div>
  )
}
